import { Collapse } from '@mui/material';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Icon, Paper, Typography } from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { UseCollapseReturn } from './menu/MobileMenu';

import {typeTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  menuItem: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  fullHeightItem: {
    zIndex: 1,
    cursor: 'pointer',
    userSelect: 'none',
    msUserSelect: 'none',
    MozUserSelect: 'none',
    WebkitUserSelect: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    backgroundColor: theme.color.background.bgAlt,
  },
  menuActiveButton: {
    borderRadius: 16,
    backgroundColor: theme.color.background.bgMain,
  },
  menuLabel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.color.text.textTertiary,
    // @ts-ignore
     ...typeTheme(theme, 'body1bold'),
  },
  menuActiveLabel: {
    color: theme.color.text.textHeader,
  },
  edgeAnchor: {
    position: 'relative',
  },
  clipperMenuPaper: {
    position: 'absolute',
    width: '100%',
    top: '-28px',
    height: 28,
    zIndex: 0,
    backgroundColor: theme.color.background.bgInverse01,
    borderRadius: 0,
  },
  menuPaper: {
    backgroundColor: theme.color.background.bgInverse01,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    boxShadow: theme.shadow.shadow3,
  },
  subMenuItemGroupLabelContainer: {
    color: theme.color.text.textContrast,
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: theme.color.border.borderInverse,
  },
  activeSubMenuItem: {
    color: theme.color.text.textContrast,
  },
  subMenuItemNoLabel: {
    color: theme.color.text.textContrast,
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: theme.color.border.borderInverse,
  },
  subMenuItem: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    color: theme.color.text.textInactive,
    '&:hover': {
      color: theme.color.text.textContrast,
      backgroundColor: 'rgba(232, 232, 232, 0.3)',
    },
  },
  subMenuLabel: {
    fontWeight: 'normal',
    userSelect: 'none',
    msUserSelect: 'none',
    MozUserSelect: 'none',
    WebkitUserSelect: 'none',
  },
  menuItemIcon: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  noneTopBorder: {
    borderTopWidth: 0,
  },
}));

interface MenuCollapseItemProps extends UseCollapseReturn {
  label: string;
  isActiveMenu: boolean;
  subMap: (
    | {
      id?: string;
      label: string;
      navigate: () => void;
      isActiveSubMenu: boolean;
    }
    | string
  )[];
}

export const MenuCollapseItem = ({
  anchorEl,
  handleClick,
  handleClose,
  label,
  subMap,
  isActiveMenu,
}: MenuCollapseItemProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const location = useLocation();

  const activeButtonStyle = anchorEl ? classes.menuActiveButton : '';
  const activeLabelStyle =
    location.pathname === '/' || isActiveMenu || anchorEl ? classes.menuActiveLabel : '';

  return (
    <Box className={classes.menuItem}>
      <Box className={[classes.fullHeightItem, activeButtonStyle].join(' ')} onClick={handleClick}>
        <Typography
          // @ts-ignore
          id="menu-label"
          className={[classes.menuLabel, activeLabelStyle].join(' ')}
          variant="subtitle1"
        >
          {label}
          <Icon
            size="S"
            className={classes.menuItemIcon}
            color={theme.color.icon.iconDefault}
            name={IconName.arrowDown}
          />
        </Typography>
      </Box>

      <Box className={classes.edgeAnchor}>
        <Paper className={classes.menuPaper}>
          <Collapse in={Boolean(anchorEl)}>
            <Box className={classes.clipperMenuPaper}>
              <></>
            </Box>
            {subMap.map((item, index) => {
              if (typeof item === 'string') {
                if (item === '') {
                  return <Box key={item} className={classes.subMenuItemNoLabel} />;
                }
                return (
                  <Box
                    key={item}
                    className={[
                      classes.subMenuItemGroupLabelContainer,
                      index === 0 ? classes.noneTopBorder : '',
                    ].join(' ')}
                  >
                    <Typography variant="h5">{item}</Typography>
                  </Box>
                );
              }

              const activeSubMenuStyle = item.isActiveSubMenu ? classes.activeSubMenuItem : '';
              return (
                <Box
                  id={item.id}
                  // eslint-disable-next-line react/no-array-index-key
                  key={item.id || item.label}
                  className={classes.subMenuItem}
                  onClick={() => {
                    item.navigate();
                    handleClose();
                  }}
                >
                  <Typography
                    variant="caption"
                    className={[classes.subMenuLabel, activeSubMenuStyle].join(' ')}
                  >
                    {item.label}
                  </Typography>
                </Box>
              );
            })}
          </Collapse>
        </Paper>
      </Box>
    </Box>
  );
};

export default MenuCollapseItem;
